<!--
  AI:
  Status: "in progress"
  Type: Component
  SubType: CrudIntegration
  Reason: "Weitere" navigation point — catch-all config tabs. Every tab body is @if-guarded on the
    active tab (CONFIG-022).
-->
<div class="cw-others p-2">
    <p-tabs [value]="activeTab" (valueChange)="onTabChange($event)" [scrollable]="true">
        <p-tablist>
            <p-tab value="externalOperations">Externe Operationen</p-tab>
            <!-- [CW-STEP-ACTIONS] What the step engine does when a step is completed (V1: a notification) -->
            <p-tab value="stepActions">Schritt-Aktionen</p-tab>
            <p-tab value="messageMapping">Meldungs-Mapping</p-tab>
            <p-tab value="applicationTemplates">Antragstemplates</p-tab>
            <p-tab value="textBlocks">Textbausteine</p-tab>
            <!-- [CW-VP-DIM] Vorbelegungen (Vorschläge) dieses Antragstyps -->
            <p-tab value="caseTypeDefaults">Vorbelegungen</p-tab>
            <!-- [CW-TAA-V3] Who signs which role of the application -->
            <p-tab value="signatureRoleSources">Unterschriftsrollen</p-tab>
            <!-- [CW-TICKET-TYPES] Which ticket types a Vorgang may raise, Nachbearbeitung / Sonstiges -->
            <p-tab value="ticketTypes">Tickettypen</p-tab>
        </p-tablist>

        <p-tabpanels>

            <!--
              [CW-TAA-V3] External operations as the master, below them the step buttons of the
              selected operation (n:m). The operation's own 1:1 step binding stays a COLUMN of the
              upper table — the lower table adds FURTHER steps on which the same operation appears
              as a button.
            -->
            <p-tabpanel value="externalOperations">
                @if (activeTab === 'externalOperations') {
                    @if (externalOperationWidget) {
                        <mvs-widget class="block mb-3"
                                    [widgetData]="externalOperationWidget"
                                    (onObjectSelect)="handleExternalOperationSelect($event)">
                        </mvs-widget>
                    }
                    @if (selectedExternalOperationId) {
                        @if (externalOperationStepWidget) {
                            <mvs-widget [widgetData]="externalOperationStepWidget"></mvs-widget>
                        }
                    } @else {
                        <div class="cw-hint p-4 text-center text-500">
                            Bitte eine externe Operation wählen, um ihre Schaltflächen auf weiteren Schritten zu
                            konfigurieren.
                        </div>
                    }
                }
            </p-tabpanel>

            <!--
              [CW-STEP-ACTIONS] Schritt-Aktionen: a flat list per case type, sorted by Reihenfolge. On create
              the case type is locked, the Reihenfolge is prefilled, and step and notification type offer only
              the rows of this case type — none at all when the case type has none (F-1), which the hints
              below say before the user opens the form. A list that could not be read refuses „Anlegen".
            -->
            <p-tabpanel value="stepActions">
                @if (activeTab === 'stepActions') {
                    <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary">
                        <i class="fa-regular fa-bolt mt-1"></i>
                        <span class="line-height-3">
                            Aktionen, die beim Abschließen eines Schritts automatisch ausgeführt werden (z. B. E-Mail
                            an die Gesellschaft).
                        </span>
                    </div>
                    @if (stepActionIdLoadFailed) {
                        <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary"
                             data-cw-step-action-hint="load-failed">
                            <i class="fa-regular fa-triangle-exclamation text-orange-500 mt-1"></i>
                            <div class="flex flex-column align-items-start gap-2">
                                <span class="line-height-3">{{ idLoadFailedHint }}</span>
                                <p-button label="Erneut laden"
                                          icon="fa-regular fa-rotate"
                                          severity="secondary"
                                          [text]="true"
                                          size="small"
                                          (onClick)="reloadStepActionIdLists()">
                                </p-button>
                            </div>
                        </div>
                    }
                    @if (hasNoSteps) {
                        <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary"
                             data-cw-step-action-hint="no-step">
                            <i class="fa-regular fa-circle-info mt-1"></i>
                            <span class="line-height-3">{{ noStepHint }}</span>
                        </div>
                    }
                    @if (hasNoNotificationTypes) {
                        <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary"
                             data-cw-step-action-hint="no-notification-type">
                            <i class="fa-regular fa-circle-info mt-1"></i>
                            <span class="line-height-3">{{ noNotificationTypeHint }}</span>
                        </div>
                    }
                    @if (stepActionWidget) {
                        <mvs-widget [widgetData]="stepActionWidget"></mvs-widget>
                    }
                }
            </p-tabpanel>

            <!--
              #C.1: die Tabelle zeigt die VEREINIGUNG aus den Katalogzeilen dieses Vorgangstyps und
              den Zeilen, die er von seiner Partei-Version erbt. Geerbte Zeilen tragen das
              Kennzeichen „Partei-Version“ und werden hier NICHT bearbeitet.
            -->
            <p-tabpanel value="messageMapping">
                @if (activeTab === 'messageMapping') {

                    @if (hasInheritedScope()) {
                        <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary">
                            <i class="fa-solid fa-circle-info mt-1"></i>
                            <div class="flex flex-column align-items-start gap-2">
                                <span>{{ scopeInheritanceHint }}</span>
                                <!-- Der Hinweis nennt die Fläche nicht nur, er öffnet sie MIT der
                                     Partei-Version dieses Vorgangstyps bereits ausgewaehlt. -->
                                <p-button [label]="openPartyVersionConfigLabel"
                                          icon="fa-solid fa-layer-group"
                                          severity="secondary"
                                          [text]="true"
                                          size="small"
                                          (onClick)="openPartyVersionConfig()">
                                </p-button>
                            </div>
                        </div>
                        <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary">
                            <i class="fa-solid fa-list-ol mt-1"></i>
                            <span>{{ scopePrecedenceHint }}</span>
                        </div>
                    }

                    @if (externalMessageMappingWidget) {
                        <mvs-widget [widgetData]="externalMessageMappingWidget">
                            <!--
                              Geltungsbereich-Spalte: die fieldId MUSS CW_SCOPE_CUSTOM_FIELD_ID sein,
                              sonst findet widget.service.ts kein Template und lässt die Spalte
                              KOMMENTARLOS weg (isCustomTemplateField -> continue).
                            -->
                            <ng-template mvs-widget-custom-field-directive
                                         area="columns"
                                         [fieldId]="scopeCustomFieldId"
                                         [label]="scopeColumnLabel"
                                         let-object="object">
                                <mvs-cw-carrier-scope-badge [object]="object"></mvs-cw-carrier-scope-badge>
                            </ng-template>
                        </mvs-widget>
                    }
                }
            </p-tabpanel>

            <p-tabpanel value="applicationTemplates">
                @if (activeTab === 'applicationTemplates' && applicationTemplateWidget) {
                    <mvs-widget [widgetData]="applicationTemplateWidget"></mvs-widget>
                }
            </p-tabpanel>

            <p-tabpanel value="textBlocks">
                @if (activeTab === 'textBlocks' && textBlockWidget) {
                    <mvs-widget [widgetData]="textBlockWidget"></mvs-widget>
                }
            </p-tabpanel>

            <!--
              [CW-VP-DIM] Vorbelegungen: reine Einstiegsliste — ein Klick öffnet die eigene
              Objektseite des Vorschlags (Kopf + Baustein-Zeilen + Feldwert-Vorschau), weil ein
              Vorschlag Kindzeilen und alias-basierte Feldwerte besitzt (PO-Entscheid Q7).
            -->
            <p-tabpanel value="caseTypeDefaults">
                @if (activeTab === 'caseTypeDefaults' && caseTypeDefaultWidget) {
                    <mvs-widget
                            [widgetData]="caseTypeDefaultWidget"
                            (onObjectSelect)="handleCaseTypeDefaultSelect($event)">
                    </mvs-widget>
                }
            </p-tabpanel>

            <!--
              [CW-TAA-V3] Signature roles: a flat list filtered on the case type. The person type is
              a COLUMN of the row and not a selection axis above it — it is optional, and read only
              when the source is Person.
            -->
            <p-tabpanel value="signatureRoleSources">
                @if (activeTab === 'signatureRoleSources') {
                    <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary">
                        <i class="fa-solid fa-signature mt-1"></i>
                        <span class="line-height-3">
                            Mehrere Zeilen dürfen <strong>dieselbe Unterschriftsrolle</strong> tragen. Sie werden
                            als <strong>Kette</strong> in der Reihenfolge abgearbeitet — die erste Zeile, die eine
                            Person auflöst, gewinnt; die übrigen werden nicht mehr geprüft. Die Tabelle ist deshalb
                            nach Rolle und dann nach Reihenfolge sortiert, damit die Kette zusammenhängend sichtbar
                            bleibt. Löst <em>keine</em> Zeile einer Rolle auf, gilt die Unterschrift für diesen
                            Vorgang als nicht erforderlich; eine Rolle ganz <em>ohne</em> Zeile bleibt dagegen
                            unkonfiguriert und muss von Hand erfasst werden.
                        </span>
                    </div>
                    @if (signatureRoleSourceWidget) {
                        <mvs-widget [widgetData]="signatureRoleSourceWidget"></mvs-widget>
                    }
                }
            </p-tabpanel>

            <!--
              [CW-TICKET-TYPES] Tickettypen: flache Liste je Vorgangstyp. Der interne Typ entscheidet, ob ein
              Ticket dieses Typs den Rückfragen-Status und die Sperren des Vorgangs bestimmt
              (Nachbearbeitung) oder nur „Niedrigster Status sonstige Tickets“ (Sonstiges).
            -->
            <p-tabpanel value="ticketTypes">
                @if (activeTab === 'ticketTypes') {
                    <div class="flex align-items-start gap-2 p-3 mb-2 border-round surface-100 text-color-secondary">
                        <i class="fa-solid fa-ticket mt-1"></i>
                        <span class="line-height-3">
                            <strong>Nachbearbeitung</strong>: Rückfrage-Tickets — sie bestimmen den Rückfragen-Status
                            und sperren den Abschluss des Vorgangs, solange sie offen sind. <strong>Sonstiges</strong>:
                            alle übrigen Tickets zum Vorgang — ohne Einfluss auf Status und Sperren. Die Objektrolle
                            Vorgang muss eine Hauptobjekt-Rolle sein, sonst erscheint das Ticket nicht in der
                            Ticketliste des Vorgangs. Solange keine aktive Zeile „Nachbearbeitung“ existiert, gilt
                            weiterhin der Tickettyp aus den Grunddaten des Vorgangstyps.
                        </span>
                    </div>
                    @if (ticketTypeWidget) {
                        <mvs-widget [widgetData]="ticketTypeWidget"></mvs-widget>
                    }
                }
            </p-tabpanel>

        </p-tabpanels>
    </p-tabs>
</div>
